import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';

import * as graphql from 'generated/graphql';
import { SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';

import MultiMetric from './metrics/MultiMetric';
import ScalarMetric from './metrics/ScalarMetric';
import SessionsOverTimeMetric from './metrics/sessions/SessionsOverTimeMetric';
import DashboardCard from './DashboardCard';
import GoalsStatusMetric from './metrics/goals/GoalsStatusMetric';
import SubgoalsCountMetric from './metrics/goals/SubgoalsCountMetric';
import GoalMomentsCompletedMetric from './metrics/goals/GoalMomentsCompletedMetric';
import AveragePerUserMetric from './metrics/goals/AveragePerUserMetric';
import FeedbackStatusOverTimeMetric from './metrics/feedback/FeedbackStatusOverTimeMetric';
import FeedbackOnTimeMetric from './metrics/feedback/FeedbackOnTimeMetric';
import FeedbackMomentsCompletedMetric from './metrics/feedback/FeedbackMomentsCompletedMetric';
import FeedbackInternalExternalMetric from './metrics/feedback/FeedbackInternalExternalMetric';
import FeedbackCategoryRatingMetric from './metrics/feedback/FeedbackCategoryRatingMetric';
import EvaluationSentimentMetric from './metrics/evaluations/EvaluationSentimentMetric';
import { Button, Callout, Container, Jumbo, Tabs } from '@treams/treams-ui';
import { Link } from '@treams/treams-routes';

const Column = styled(Box)`
	flex-shrink: 0;
`;

export default function DashboardOrganization(props: { filters: graphql.AnalyticsFilters }) {
	const { t } = useTranslation();

	const useFeedback = useFeature('2021.feedback');
	const useGoals = useFeature('2021.goals');
	const useCycles = useFeature('2021.cycles');
	const useEngagement = useFeature('2022.engagement');

	const bucketSize = graphql.AnalyticsBucketSize.Week;

	const { data, loading } = graphql.useOrganizationMetricsQuery({
		variables: {
			filters: props.filters,
			bucketSize,
		},
	});

	if (loading || !data) {
		return <Spinner isLoading size="18px" />;
	}

	const {
		sessionsOverTime,
		feedbackStatusCountsOverTime,
		feedbackResponsesOnTime,
		feedbackMomentsCompleted,
		feedbackInternalExternal,
		feedbackAverageCategoryRating,
		goalCounts,
		subgoalCounts,
		goalMomentsCompleted,
		averageGoalCount,
		archivedWithoutCompletion,
		sentimentCounts,
	} = data.workspaceAnalytics;

	return (
		<Box>
			<Jumbo
				title={t('dashboard.organization.title')}
				wide
				footer={
					<>
						<Container wide>
							<Link to="/analytics/v2">
								<Callout color="primary">
									<Box>
										<Box mb={2}>{t('dashboard.v2.upgrade')}</Box>
										<Flex>
											<Button type="card" title={t('dashboard.v2.upgradeButton')} small />
										</Flex>
									</Box>
								</Callout>
							</Link>
						</Container>
						{useEngagement ? (
							<Tabs.Container wide>
								<Tabs>
									<Tabs.Item title={t('dashboard.organization.title')} selected path="/analytics" />
									<Tabs.Item title={t('dashboard.engagement.title')} path="/analytics/engagement" />
								</Tabs>
							</Tabs.Container>
						) : null}
					</>
				}
			/>
			<Box p={[3, 3, 4]}>
				<Box mb={4}>
					<DashboardCard
						title={t('dashboard.metrics.sessionsOverTime.title')}
						description={t(
							`dashboard.metrics.sessionsOverTime.description.${bucketSize.toLowerCase()}`,
						)}
						explanation={t('dashboard.metrics.sessionsOverTime.explanation')}
					>
						<SessionsOverTimeMetric
							bucketSize={bucketSize}
							loading={loading}
							data={sessionsOverTime}
						/>
					</DashboardCard>
				</Box>
				<Flex flexWrap="wrap">
					<Column width={[1, 1, 1 / 2]}>
						<Box mr={[0, 0, 2]}>
							{useFeedback && (
								<>
									<Box mb={2}>
										<SubSectionTitle>{t('dashboard.metricGroups.feedback.title')}</SubSectionTitle>
									</Box>
									<Box mb={3}>
										<DashboardCard
											description={t(
												`dashboard.metrics.feedbackStatusOverTime.description.${bucketSize.toLowerCase()}`,
											)}
										>
											<FeedbackStatusOverTimeMetric
												bucketSize={bucketSize}
												data={feedbackStatusCountsOverTime}
											/>
										</DashboardCard>
									</Box>
									<Box mb={3}>
										<DashboardCard>
											<MultiMetric>
												<FeedbackOnTimeMetric data={feedbackResponsesOnTime} />
												{useCycles && (
													<FeedbackMomentsCompletedMetric data={feedbackMomentsCompleted} />
												)}
											</MultiMetric>
										</DashboardCard>
									</Box>
									<Box mb={3}>
										<DashboardCard
											title={t('dashboard.metrics.feedbackInternalExternal.title')}
											description={t('dashboard.metrics.feedbackInternalExternal.description')}
										>
											<FeedbackInternalExternalMetric data={feedbackInternalExternal} />
										</DashboardCard>
									</Box>
									<Box mb={3}>
										<FeedbackCategoryRatingMetric
											data={
												feedbackAverageCategoryRating as graphql.AnalyticsFeedbackAverageRating[]
											}
										/>
									</Box>
								</>
							)}
							<Box mb={3}>
								<DashboardCard
									title={t('dashboard.metrics.sentimentCounts.title')}
									description={t('dashboard.metrics.sentimentCounts.description')}
									explanation={t('dashboard.metrics.sentimentCounts.explanation')}
								>
									<EvaluationSentimentMetric data={sentimentCounts} />
								</DashboardCard>
							</Box>
						</Box>
					</Column>
					{useGoals && (
						<Column width={[1, 1, 1 / 2]}>
							<Box ml={[0, 0, 2]}>
								<Box mb={2}>
									<SubSectionTitle>{t('dashboard.metricGroups.goals.title')}</SubSectionTitle>
								</Box>
								<Box mb={3}>
									<GoalsStatusMetric counts={goalCounts} />
								</Box>
								<Box mb={3}>
									<DashboardCard>
										<MultiMetric>
											<SubgoalsCountMetric data={subgoalCounts} />
											{useCycles && <GoalMomentsCompletedMetric data={goalMomentsCompleted} />}
										</MultiMetric>
									</DashboardCard>
								</Box>
								<Box mb={3}>
									<DashboardCard>
										<MultiMetric>
											<ScalarMetric
												loading={false}
												title={t('dashboard.metrics.goalsArchivedWithoutCompletion.title')}
												value={`${archivedWithoutCompletion}`}
											/>
											<AveragePerUserMetric data={averageGoalCount} />
										</MultiMetric>
									</DashboardCard>
								</Box>
							</Box>
						</Column>
					)}
				</Flex>
			</Box>
		</Box>
	);
}
